import React, { useEffect, useState, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';

import backendLogo from '../../assets/images/logo/backend.png';
import frontendLogo from '../../assets/images/logo/angular.svg';
import githubLogo from '../../assets/images/logo/github.png';
import gmailLogo from '../../assets/images/logo/gmail.png';
import mongoLogo from '../../assets/images/logo/mongo.png';
import razorpayLogo from '../../assets/images/logo/razorpay.png';
import testingLogo from '../../assets/images/logo/testing.png';

const capabilities = [
    { id: 1, name: 'GitHub', icon: githubLogo },
    { id: 2, name: 'MongoDB', icon: mongoLogo },
    { id: 3, name: 'PAYMENTS', icon: razorpayLogo },
    { id: 4, name: 'Testing', icon: testingLogo },
    { id: 5, name: 'Gmail', icon: gmailLogo },
    { id: 6, name: 'Backend', icon: backendLogo },
    { id: 7, name: 'Frontend', icon: frontendLogo },
];

// Starting positions (percentage of container)
const startPositions = [
    { x: 15, y: 8 },
    { x: 60, y: 5 },
    { x: 8, y: 32 },
    { x: 72, y: 35 },
    { x: 18, y: 56 },
    { x: 66, y: 60 },
    { x: 38, y: 80 },
];

// Physics constants
const CARD_PX = 65;            // card width/height in pixels
const SPEED = 0.12;           // base drift speed per frame
const MAX_SPEED = 0.35;       // max speed clamp
const DIRECTION_CHANGE = 0.006;
const BOUNCE_ENERGY = 0.85;   // energy preserved on collision
const BOUNDS = { minX: 0, maxX: 88, minY: 0, maxY: 86 };

const RAZORPAY_INDEX = 2; // Index of the Razorpay card in capabilities[]

export const PluginGarden = forwardRef(({ activePluginIndex = 2 }, ref) => {
    const [visiblePlugins, setVisiblePlugins] = useState([]);

    const cardsRef = useRef([]);
    const stateRef = useRef(null);
    const rafRef = useRef(null);
    const containerRef = useRef(null);
    const frozenIndexRef = useRef(null); // Which card index is frozen (null = none)

    // Expose imperative API for TravelingHand to read Razorpay position & freeze it
    useImperativeHandle(ref, () => ({
        /** Get Razorpay card's current viewport bounding rect */
        getRazorpayRect: () => {
            const el = cardsRef.current[RAZORPAY_INDEX];
            return el ? el.getBoundingClientRect() : null;
        },
        /** Get Razorpay card DOM element */
        getRazorpayEl: () => cardsRef.current[RAZORPAY_INDEX],
        /** Freeze Razorpay card — stops its physics motion */
        freeze: () => { frozenIndexRef.current = RAZORPAY_INDEX; },
        /** Unfreeze Razorpay card — resumes physics motion */
        unfreeze: () => { frozenIndexRef.current = null; },
    }));

    const initState = useCallback(() => {
        return capabilities.map((_, i) => {
            const angle = Math.random() * Math.PI * 2;
            return {
                x: startPositions[i].x,
                y: startPositions[i].y,
                vx: Math.cos(angle) * SPEED,
                vy: Math.sin(angle) * SPEED,
            };
        });
    }, []);

    useEffect(() => {
        capabilities.forEach((_, index) => {
            setTimeout(() => {
                setVisiblePlugins(prev => [...prev, index]);
            }, 600 + index * 150);
        });

        return () => {};
    }, []);

    // Physics animation loop with collision detection
    useEffect(() => {
        stateRef.current = initState();

        const clampSpeed = (vx, vy) => {
            const speed = Math.sqrt(vx * vx + vy * vy);
            if (speed > MAX_SPEED) {
                return { vx: (vx / speed) * MAX_SPEED, vy: (vy / speed) * MAX_SPEED };
            }
            if (speed < SPEED * 0.3) {
                const angle = Math.atan2(vy, vx);
                return { vx: Math.cos(angle) * SPEED * 0.5, vy: Math.sin(angle) * SPEED * 0.5 };
            }
            return { vx, vy };
        };

        const animate = () => {
            const states = stateRef.current;
            if (!states) return;
            const n = states.length;

            // Get actual container size to compute card collision radius in %
            const container = containerRef.current;
            const cw = container ? container.offsetWidth : 400;
            const ch = container ? container.offsetHeight : 500;
            const cardW = (CARD_PX / cw) * 100;  // card width as % of container
            const cardH = (CARD_PX / ch) * 100;  // card height as % of container

            // --- Card-to-card collision detection ---
            for (let i = 0; i < n; i++) {
                if (i === frozenIndexRef.current) continue; // skip frozen
                for (let j = i + 1; j < n; j++) {
                    if (j === frozenIndexRef.current) continue; // skip frozen
                    const a = states[i];
                    const b = states[j];

                    // Normalize to square space for proper circular collision
                    const dx = (b.x - a.x);
                    const dy = (b.y - a.y) * (cw / ch); // scale Y so collision is uniform
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    const minDist = cardW * 1.15; // edge-to-edge = full card width + small margin

                    if (dist < minDist && dist > 0.01) {
                        // Normalized collision axis (in real % space)
                        const rawDx = b.x - a.x;
                        const rawDy = b.y - a.y;
                        const rawDist = Math.sqrt(rawDx * rawDx + rawDy * rawDy);
                        const nx = rawDist > 0.01 ? rawDx / rawDist : 1;
                        const ny = rawDist > 0.01 ? rawDy / rawDist : 0;

                        // Relative velocity along collision axis
                        const dvx = a.vx - b.vx;
                        const dvy = a.vy - b.vy;
                        const relVel = dvx * nx + dvy * ny;

                        // Only resolve if moving toward each other
                        if (relVel > 0) {
                            const impulse = relVel * BOUNCE_ENERGY;

                            // Apply impulse (equal mass)
                            a.vx -= impulse * nx;
                            a.vy -= impulse * ny;
                            b.vx += impulse * nx;
                            b.vy += impulse * ny;

                            // Add a tiny random spin so collisions feel organic
                            a.vx += (Math.random() - 0.5) * 0.04;
                            a.vy += (Math.random() - 0.5) * 0.04;
                            b.vx += (Math.random() - 0.5) * 0.04;
                            b.vy += (Math.random() - 0.5) * 0.04;
                        }

                        // Separate overlapping cards — push apart to edge-to-edge
                        const targetSep = minDist;
                        const sepRatio = (targetSep - dist) / 2 + 0.5;
                        a.x -= sepRatio * nx;
                        a.y -= sepRatio * ny;
                        b.x += sepRatio * nx;
                        b.y += sepRatio * ny;
                    }
                }
            }

            // --- Update each card ---
            for (let i = 0; i < n; i++) {
                // Skip frozen card (being dragged by cursor)
                if (i === frozenIndexRef.current) continue;

                const s = states[i];

                // Random drift nudge
                if (Math.random() < DIRECTION_CHANGE) {
                    s.vx += (Math.random() - 0.5) * 0.25;
                    s.vy += (Math.random() - 0.5) * 0.25;
                }

                // Clamp speed
                const clamped = clampSpeed(s.vx, s.vy);
                s.vx = clamped.vx;
                s.vy = clamped.vy;

                // Move
                s.x += s.vx;
                s.y += s.vy;

                // Wall collisions — bounce off edges
                if (s.x < BOUNDS.minX) {
                    s.x = BOUNDS.minX;
                    s.vx = Math.abs(s.vx) * (0.7 + Math.random() * 0.3);
                    s.vy += (Math.random() - 0.5) * 0.1;
                }
                if (s.x > BOUNDS.maxX) {
                    s.x = BOUNDS.maxX;
                    s.vx = -Math.abs(s.vx) * (0.7 + Math.random() * 0.3);
                    s.vy += (Math.random() - 0.5) * 0.1;
                }
                if (s.y < BOUNDS.minY) {
                    s.y = BOUNDS.minY;
                    s.vy = Math.abs(s.vy) * (0.7 + Math.random() * 0.3);
                    s.vx += (Math.random() - 0.5) * 0.1;
                }
                if (s.y > BOUNDS.maxY) {
                    s.y = BOUNDS.maxY;
                    s.vy = -Math.abs(s.vy) * (0.7 + Math.random() * 0.3);
                    s.vx += (Math.random() - 0.5) * 0.1;
                }

                // Apply to DOM
                const el = cardsRef.current[i];
                if (el) {
                    el.style.left = s.x + '%';
                    el.style.top = s.y + '%';
                }
            }

            rafRef.current = requestAnimationFrame(animate);
        };

        rafRef.current = requestAnimationFrame(animate);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [initState]);

    return (
        <div ref={containerRef} className="relative h-full w-full" data-testid="plugin-garden">
            {capabilities.map((capability, index) => {
                const isVisible = visiblePlugins.includes(index);
                const isActive = index === activePluginIndex;

                return (
                    <div
                        key={capability.id}
                        ref={el => cardsRef.current[index] = el}
                        data-testid={`plugin-card-${capability.id}`}
                        className="absolute"
                        style={{
                            top: startPositions[index].y + '%',
                            left: startPositions[index].x + '%',
                            opacity: isVisible ? 1 : 0,
                            transition: 'opacity 0.5s ease',
                            zIndex: isActive ? 10 : 1,
                            willChange: 'left, top',
                        }}
                    >
                        <div style={{
                            width: '65px',
                            height: '65px',
                            background: '#ffffff',
                            border: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.12)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <img
                                src={capability.icon}
                                alt={capability.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }}
                            />
                        </div>
                        <p className="text-center mt-1 text-[10px] font-bold" style={{ color: '#2d8a28' }}>
                            {capability.name}
                        </p>
                    </div>
                );
            })}

            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[...Array(8)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-1 h-1 rounded-full bg-[#9be38f]"
                        style={{
                            left: (10 + Math.random() * 80) + '%',
                            top: (10 + Math.random() * 80) + '%',
                            opacity: 0.2,
                            animation: 'particle-drift ' + (6 + Math.random() * 4) + 's linear infinite',
                            animationDelay: (Math.random() * 4) + 's'
                        }}
                    />
                ))}
            </div>
        </div>
    );
});

export default PluginGarden;
